export const ORGANIZATION_SWITCH_TIMEOUT_MS = 7_000;

export type SwitchOrgMembership = {
  organization: {
    id: string;
    name: string;
    slug?: string | null;
  };
};

export type OrganizationSwitchFailure = "error" | "timeout";

export type OrganizationSwitchState =
  | { status: "idle" }
  | { status: "switching"; organizationId: string }
  | { status: "failed"; organizationId: string; failure: OrganizationSwitchFailure };

export type OrganizationSwitchEvent =
  | { type: "start"; source: "automatic" | "manual"; organizationId: string }
  | { type: "failed"; organizationId: string; failure: OrganizationSwitchFailure }
  | { type: "reset"; organizationId: string };

export const initialOrganizationSwitchState: OrganizationSwitchState = { status: "idle" };

export const canStartOrganizationSwitch = (state: OrganizationSwitchState, source: "automatic" | "manual") =>
  state.status === "idle" || (source === "manual" && state.status === "failed");

export const organizationSwitchStateReducer = (state: OrganizationSwitchState, event: OrganizationSwitchEvent): OrganizationSwitchState => {
  switch (event.type) {
    case "start":
      if (!canStartOrganizationSwitch(state, event.source)) {
        return state;
      }
      return { status: "switching", organizationId: event.organizationId };
    case "failed":
      if (state.status !== "switching" || state.organizationId !== event.organizationId) {
        return state;
      }
      return { status: "failed", organizationId: event.organizationId, failure: event.failure };
    case "reset":
      if (state.status !== "switching" || state.organizationId !== event.organizationId) {
        return state;
      }
      return initialOrganizationSwitchState;
  }
};

export const getOrganizationSwitchFailure = (error: unknown): OrganizationSwitchFailure =>
  error instanceof OrganizationSwitchTimeoutError ? "timeout" : "error";

export const getOrganizationSwitchFailureMessage = (failure: OrganizationSwitchFailure): string =>
  failure === "timeout"
    ? "Switching organizations is taking longer than expected. Please select an organization to try again."
    : "Unable to switch organizations. Please select an organization to try again.";

export const hasAuthoritativePaginatedMemberships = (args: {
  organizationListLoaded: boolean;
  membershipsError: boolean;
  membershipsLoading: boolean;
}): boolean => args.organizationListLoaded && !args.membershipsLoading && !args.membershipsError;

export const shouldShowMembershipLoadError = (args: {
  fallbackMemberships: readonly SwitchOrgMembership[];
  membershipsError: boolean;
  userLoaded: boolean;
}): boolean => args.userLoaded && args.membershipsError && args.fallbackMemberships.length === 0;

export const shouldShowPartialMembershipLoadError = (args: {
  fallbackMemberships: readonly SwitchOrgMembership[];
  membershipsError: boolean;
  userLoaded: boolean;
}): boolean => args.userLoaded && args.membershipsError && args.fallbackMemberships.length > 0;

export const shouldShowPartialMembershipsLoading = (args: {
  fallbackMemberships: readonly SwitchOrgMembership[];
  membershipsError: boolean;
  paginatedMembershipsLoaded: boolean;
  userLoaded: boolean;
}): boolean => args.userLoaded && args.fallbackMemberships.length > 0 && !args.paginatedMembershipsLoaded && !args.membershipsError;

export class OrganizationSwitchTimeoutError extends Error {
  constructor() {
    super("Organization switch timed out.");
    this.name = "OrganizationSwitchTimeoutError";
  }
}

export const withOrganizationSwitchTimeout = async <T>(operation: () => Promise<T>): Promise<T> => {
  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_resolve, reject) => {
    timer = setTimeout(() => reject(new OrganizationSwitchTimeoutError()), ORGANIZATION_SWITCH_TIMEOUT_MS);
  });

  try {
    return await Promise.race([operation(), timeout]);
  } finally {
    if (timer !== null) {
      clearTimeout(timer);
    }
  }
};

const sortMemberships = (memberships: readonly SwitchOrgMembership[]): SwitchOrgMembership[] =>
  memberships.slice().sort((a, b) => a.organization.name.localeCompare(b.organization.name));

// Clerk serves `userMemberships` one page at a time and ignores a pageSize above its own maximum,
// so that page can hold fewer memberships than the user resource already carries. Merge the two
// sources by organization id — letting the page win would drop every membership past it from the
// switcher and from redirect recovery.
export const getSortedSwitchOrgMemberships = (args: {
  userMemberships: readonly SwitchOrgMembership[];
  paginatedMemberships: readonly SwitchOrgMembership[];
}): SwitchOrgMembership[] => {
  const byOrganizationId = new Map<string, SwitchOrgMembership>();
  for (const membership of args.userMemberships) {
    byOrganizationId.set(membership.organization.id, membership);
  }
  for (const membership of args.paginatedMemberships) {
    byOrganizationId.set(membership.organization.id, membership);
  }
  return sortMemberships([...byOrganizationId.values()]);
};

const matchesRedirectOrg = (membershipSlug: string | null | undefined, redirectOrgSlug: string) => {
  if (!membershipSlug) {
    return false;
  }
  return membershipSlug === redirectOrgSlug || `org-${membershipSlug}` === redirectOrgSlug;
};

export const findRedirectOrgMembership = (memberships: readonly SwitchOrgMembership[], redirectOrgSlug: string) =>
  memberships.find(x => matchesRedirectOrg(x.organization.slug, redirectOrgSlug));

export const findRequestedOrganizationSwitchMembership = (args: {
  memberships: readonly SwitchOrgMembership[];
  redirectOrgSlug: string | undefined;
  retryOrganizationId: string | undefined;
}) => {
  if (args.retryOrganizationId) {
    return args.memberships.find(membership => membership.organization.id === args.retryOrganizationId);
  }
  if (!args.redirectOrgSlug) {
    return undefined;
  }
  return findRedirectOrgMembership(args.memberships, args.redirectOrgSlug);
};

export const getOrganizationSwitchRetryUrl = (args: { organizationId: string; redirectUrl: string }) => {
  const searchParams = new URLSearchParams({
    redirect_url: args.redirectUrl,
    retry_org_id: args.organizationId,
  });
  return `/switch-org?${searchParams.toString()}`;
};

export const shouldShowRedirectOrgNoMatch = (args: {
  memberships: readonly SwitchOrgMembership[];
  redirectOrgSlug: string | undefined;
  membershipsLoaded: boolean;
}) => !!args.redirectOrgSlug && args.membershipsLoaded && !findRedirectOrgMembership(args.memberships, args.redirectOrgSlug);
